import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import useQueryString from "../hooks/useQueryString";

import { formatter } from '../helpers/Strings';

import AppContext from './context/AppContext'
import SearchContext from './context/SearchContext'
import CarCard from './partials/CarCard'
import Paging from './partials/Paging'
import Filter from './partials/Filter'
import Subscription from './partials/car/Subscription';
import Placeholder from './partials/Placeholder';
import WPBlocks from './partials/WPBlocks';
import StickyHeader from './partials/StickyHeader';
import MobileFilter from './partials/MobileFilter';
import imboxToggle from '../helpers/imboxToggle'

const Search = (props) => {

	
	const {headless, filter, container, client} = useContext(AppContext);

	// Routing debug
	let routeParams = {
		manufacturer: false,
		modelSeries: false
	}
	const incomingRouteParams = useParams() // this cannot be conditional...

	if(!headless) {
		routeParams = incomingRouteParams;
	}

	
	// Array of Car objects from API
	const [cars, setCars] = useState([])

	// Is true while fetching results
	const [loading, setLoading] = useState(false)

	// Text search array, concats to string
	// const [query, setQuery] = useState([])

	// Query string
	const [queryString, setQueryString] = useQueryString(useLocation().search);

	// API filter params
	const [params, setParams] = useState(new URLSearchParams(useLocation().search))

	// Number of results from current search
	const [carsInStock, setCarsInStock] = useState(false)
	const [carsInStockTotal, setCarsInStockTotal] = useState(false)

	// Paging, current index
	const [page, setPage] = useState(1)

	// Possible filters for API, with no of hits in each key
	const [facets, setFacets] = useState(false)

	// Number of results per page
	const hitsPerPage = 40

	// Ignore params
	const ignoreParams = ['hits', 'offset']

	const api_call = async () => {
		setLoading(true)

		const config = {
			params: {}
		}

		const defaultParams = {
			hits: hitsPerPage,
			offset: ( (params.get('page') || 1 ) - 1) * hitsPerPage,
			// query: query.join(' '),
		}

		config.params = new URLSearchParams(params.toString())


		for (const [key, value] of Object.entries(defaultParams)) {
			config.params.set(key, value)
		}

		if (headless) {
			// console.log(filter)
			config.params = new URLSearchParams(filter)
		}
		if (routeParams.manufacturer) {
			config.params.set('manufacturer', formatter(routeParams.manufacturer).query() )
		}
		if (routeParams.modelSeries) {
			config.params.set('modelSeries', formatter(routeParams.modelSeries).query() )
		}

		client.get('/search', config)
			.then(results => {
				// console.log(results)
				setCars(results.data.results)
				setCarsInStock(results.data.total)
				setCarsInStockTotal(results.data.instock)
				setFacets(results.data.facets)
				setLoading(false)
			})
			.catch(err => {
				setLoading(false)
				console.error(err)
			})

	}

	const toggleShowFilterInMobile = useCallback((force)=>{

		const activeClass = 'show-filter-mobile'

		if(force === true) {
			document.body.classList.add(activeClass)	
		} else if (force === false) {
			document.body.classList.remove(activeClass)	
		} else {
			document.body.classList.toggle(activeClass)	
		}

		if( document.body.classList.contains(activeClass)) {
			document.documentElement.style.overflow = 'hidden'
			imboxToggle().off()
		} else {
			document.documentElement.style.overflow = ''
			imboxToggle().on()
		}

	},[])


	const handleParams = useCallback((newParams, isPageChange = false) => {
		// console.log(newParams.toString());
		ignoreParams.forEach((key) => {
			newParams.delete(key)
		})
		if(!isPageChange) {
			newParams.delete('page')
		}

		if(newParams.toString() !== params.toString()) {
			setPage(1)
			setParams(newParams)
			setQueryString(newParams.toString())
			// window.scrollTo(0,0)
		}
		// console.dir({handle:'new params', params: newParams.toString()})
	},[params, ignoreParams])

	const handlePageChange = useCallback((pageNumber) => {
		const newParams = new URLSearchParams( params.toString() );
		if(pageNumber < 2) {
			newParams.delete('page')
		} else {
			newParams.set('page' , pageNumber)
		}
		handleParams(newParams, true)
		window.scrollTo(0,0)
	},[params, handleParams])


	const handleQueryParams = useCallback((query)=>{
		const newParams = new URLSearchParams(params.toString())
		// let currentQuery = newParams.get('query') ?? '';
		// currentQuery = currentQuery !== '' ? currentQuery.split(' ') : []
		// currentQuery.push(query);
		if( query === '') {
			newParams.delete( 'query')
		} else {
			newParams.set( 'query', query.trim())
		}
		handleParams(newParams)
	},[params, handleParams])



	useEffect(() => {
		api_call()
		// console.log(params)
	}, [params, queryString, page])


	useEffect(()=>{
		if(headless) { return }

		// clear out eventlisteners using clone
		let searchForm = document.querySelector('input#search')?.parentElement ?? false
		if(!searchForm) { return }


		searchForm.replaceWith(searchForm.cloneNode(true))
		searchForm = document.querySelector('input#search').parentElement

		const searchField = searchForm.querySelector('#search')
		const searchLabel = searchForm.querySelector('label')

		searchLabel.innerHTML = '<span class="strong">Sök bilar</span>';


		const qs = new URLSearchParams( queryString )
		searchField.value = qs.get('query') ?? '';

		if(searchField.value  !== '') {
			searchLabel.style = 'display:none';
		} else {
			searchLabel.style = '';
		}

		searchForm.addEventListener('submit', (e)=>{
			e.preventDefault();
			handleQueryParams(searchField.value)
			// searchField.value = ''
			searchField.blur()

			if(searchField.value  !== '') {
				searchLabel.style = 'display:none';
			} else {
				searchLabel.style = '';
			}	
		})

	},[headless, queryString])

	if (headless) {

		// Add class to containing div if in headless mode and no results were found
		if (carsInStock === 0) { container.classList.add('has-no-cars') }

		return (
			<SearchContext.Provider value={{ cars, params, handleParams, facets, carsInStock, handlePageChange, page, loading, carsInStockTotal, toggleShowFilterInMobile}}>
				<div className={'cars ' + (loading ? 'loading' : '')}>
					<div className='cars-wrapper'>
						{cars.map((car, i) => {
							return <CarCard key={`headless_${i}`} car={car} />
						})}
					</div>
				</div>
				{ carsInStock === 0 && !loading && 
					<Placeholder />
				}
			</SearchContext.Provider>
		)
	} else {
		return (
			<SearchContext.Provider value={{cars, params, handleParams, facets, carsInStock, handlePageChange, page, loading, carsInStockTotal, toggleShowFilterInMobile, hitsPerPage}}>
				<div className='App'>
					<MobileFilter />
					<div className='filter'>
						{ facets &&
							<Filter />
						}
					</div>
					<div className={'cars ' + (loading ? 'loading' : '')}>
						{ carsInStock > 0 &&
							<StickyHeader />
						} 
						{ carsInStock === 0 && !loading &&
							<Placeholder />
						}
						<div className='cars-wrapper'>
							{cars.map((car, i) => {
								return <CarCard key={`result_${i}`} car={car} />
							})}
						</div>
					</div>
					<Paging />
					{/* <Subscription /> */}
					<div id="wpblocks-section">
						<WPBlocks view="index_after" manufacturer={params.get('manufacturer') ?? false} modelSeries={params.get('modelSeries') ?? false} fuelType={params.get('fuelType') ?? false} gearboxType={params.get('gearboxType') ?? false} />
					</div>
				</div>
			</SearchContext.Provider>
		)
	}
}

export default Search
